import React from 'react';
import { Grid, Icon, Chip, Tooltip, IconButton, Collapse } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useFarmDateTime } from '../../../../hooks/useFarmDateTime';
import { TaskListStatus, TaskPlanItemType } from '../../../../models/common';
import {
  computeTotalDurationFromOccurrenceItems,
  computeExpectedEndTime,
  computePerItemEndTimesFromOccurrenceItems
} from '../../../../utils/taskTimeHelper';
import { LocationStable, LocationStableTranslations } from '../../../../models/locations';
import TaskPlanVaccinationRuleItem from '../TaskPlanVaccinationRuleItem';
import TaskPlanLibraryItem from '../TaskPlanLibraryItem';
import UpdateTaskOccurrenceModal from '../UpdateTaskOccurrenceModal';
import { HealthSystemDomainTaskLibraryResultTaskOccurrenceViewModel } from '../../../../api/HealthSystemApi';

interface IProps {
  taskPlan: HealthSystemDomainTaskLibraryResultTaskOccurrenceViewModel;
  showUpdateAction?: boolean;
  onUpdated?: () => void;
  startOfWorkingDay?: string | null;
}

const TaskPlanTableRow: React.FC<IProps> = (props) => {
  const { t } = useTranslation();
  const { formatDate, formatTime } = useFarmDateTime();

  const { taskPlan, showUpdateAction = false, onUpdated, startOfWorkingDay } = props;
  const [rowExpanded, setRowExpanded] = React.useState(false);
  const [updateModalOpen, setUpdateModalOpen] = React.useState(false);

  const expectedEndTime = React.useMemo(() => {
    if (!startOfWorkingDay || !taskPlan.items?.length) return null;
    const totalMinutes = computeTotalDurationFromOccurrenceItems(taskPlan.items);
    return computeExpectedEndTime(startOfWorkingDay, totalMinutes);
  }, [startOfWorkingDay, taskPlan.items]);

  const renderStatusCell = (taskPlan: HealthSystemDomainTaskLibraryResultTaskOccurrenceViewModel) => {
    switch (taskPlan.status) {
      case TaskListStatus.Done:
        return (
          <Chip
            variant="outlined"
            size="small"
            className="!border-0 !bg-emerald-50 !text-emerald-700"
            icon={<Icon>{'check'}</Icon>}
            label={taskPlan.completedAt && formatDate(taskPlan.completedAt) + ' ' + formatTime(taskPlan.completedAt)}
          />
        );

      case TaskListStatus.InProgress:
        return (
          <Chip
            variant="outlined"
            size="small"
            className="!border-0 !bg-amber-50 !text-amber-800"
            icon={<Icon>{'hourglass_empty'}</Icon>}
            label={t('f-tasks-lists-items-progress')}
          />
        );

      case TaskListStatus.ToDo:
        return (
          <Chip
            size="small"
            className="!border-0 !bg-slate-100 !text-slate-600"
            icon={<Icon>{'alarm'}</Icon>}
            label={t('f-tasks-lists-items-todo')}
          />
        );
    }
  };

  const perItemEndTimes = React.useMemo(() => {
    if (!taskPlan.items?.length) return [];
    return computePerItemEndTimesFromOccurrenceItems(startOfWorkingDay, taskPlan.items);
  }, [startOfWorkingDay, taskPlan.items]);

  const renderTaskListDetailsSection = (taskPlan: HealthSystemDomainTaskLibraryResultTaskOccurrenceViewModel) => {
    return (
      <Grid container direction="column">
        {taskPlan.items && (
          <Grid item container direction="column">
            {taskPlan.items.map((item, index) => (
              <Grid item key={index}>
                {item.type == TaskPlanItemType.Normal && (
                  <TaskPlanLibraryItem
                    plan={item}
                    item={item.fromTaskLibrary}
                    key={index}
                    expectedEndTime={perItemEndTimes[index]}
                  />
                )}
                {item.type == TaskPlanItemType.Vaccination && (
                  <TaskPlanVaccinationRuleItem
                    plan={item}
                    item={item.fromVaccinationRule}
                    key={index}
                    expectedEndTime={perItemEndTimes[index]}
                  />
                )}
              </Grid>
            ))}
          </Grid>
        )}

        {!taskPlan.items?.length && <Chip className="!bg-slate-100" label={t('f-tasks-lists-items-empty')} />}
      </Grid>
    );
  };

  const handleModalClose = (refreshed: boolean) => {
    setUpdateModalOpen(false);
    if (refreshed && onUpdated) {
      onUpdated();
    }
  };

  return (
    <React.Fragment key={taskPlan.id}>
      <tr className={'border-b border-slate-100 ' + (rowExpanded ? 'bg-indigo-50/60' : 'hover:bg-slate-50')}>
        <td className="px-4 py-4">
          <Tooltip arrow title={rowExpanded ? t('f-com-hide') : t('f-com-expand')}>
            <IconButton
              aria-expanded={rowExpanded}
              aria-controls={'task-details-' + taskPlan.id}
              aria-label={(rowExpanded ? t('f-com-hide') : t('f-com-expand')) + ': ' + formatDate(taskPlan.plannedDate)}
              size="small"
              onClick={() => setRowExpanded(!rowExpanded)}
            >
              {rowExpanded ? <Icon>{'keyboard_arrow_up'}</Icon> : <Icon>{'keyboard_arrow_down'}</Icon>}
            </IconButton>
          </Tooltip>
        </td>
        <td className="px-4 py-4">
          <span className="whitespace-nowrap font-medium text-slate-800">{formatDate(taskPlan.plannedDate)}</span>
          <div className="mt-1 text-xs text-slate-500">
            {t('f-task-workspace-task-count', { count: taskPlan.items?.length ?? 0, defaultValue: '{{count}} tasks' })}
          </div>
        </td>
        <td className="px-4 py-4">{t(LocationStableTranslations[taskPlan.stable as LocationStable])}</td>
        <td className="px-4 py-4 whitespace-nowrap">
          {taskPlan.employee?.name || t('f-task-workspace-unassigned', 'Unassigned')}
        </td>
        <td className="px-4 py-4 whitespace-nowrap">
          {startOfWorkingDay && expectedEndTime ? `${startOfWorkingDay} - ${expectedEndTime}` : '-'}
        </td>
        <td className="px-4 py-4 whitespace-nowrap">{renderStatusCell(taskPlan)}</td>
        {showUpdateAction && (
          <td className="px-4 py-4 whitespace-nowrap">
            <Tooltip arrow title={t('f-tasks-occurrence-update')}>
              <IconButton size="small" color="primary" onClick={() => setUpdateModalOpen(true)}>
                <Icon>edit</Icon>
              </IconButton>
            </Tooltip>
          </td>
        )}
      </tr>

      <tr>
        <td className="p-0" colSpan={showUpdateAction ? 7 : 6}>
          <Collapse in={rowExpanded} timeout="auto" unmountOnExit>
            <div id={'task-details-' + taskPlan.id} className="border-b border-slate-200 bg-slate-50 p-4 sm:px-6">
              {renderTaskListDetailsSection(taskPlan)}
            </div>
          </Collapse>
        </td>
      </tr>

      {showUpdateAction && (
        <UpdateTaskOccurrenceModal opened={updateModalOpen} taskOccurrence={taskPlan} onClose={handleModalClose} />
      )}
    </React.Fragment>
  );
};

export default TaskPlanTableRow;
